import axios from 'axios';
import { getToken } from 'firebase/app-check';
import { toast } from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import { appCheck } from '../config/firebase';

const API_URL = import.meta.env.VITE_API_URL || 'https://aps-backend-219066752787.us-central1.run.app/api';

const api = axios.create({
    baseURL: API_URL,
    timeout: 15000,
    withCredentials: true, // send httpOnly refresh cookie on every request
    headers: {
        'Content-Type': 'application/json',
    },
});

// Helper to call the refresh endpoint and get a new access token
let _refreshPromise = null;
const refreshAccessToken = () => {
    if (_refreshPromise) return _refreshPromise;
    _refreshPromise = axios.post(
        `${API_URL}/auth/refresh`,
        {},
        { withCredentials: true, timeout: 10000 }
    ).then(res => {
        const newToken = res.data?.token;
        if (newToken) localStorage.setItem('token', newToken);
        return newToken;
    }).finally(() => {
        _refreshPromise = null;
    });
    return _refreshPromise;
};

// Helper to handle forced logouts
const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    const path = window.location.pathname;
    
    // Public paths that should not be forcefully redirected to login
    const publicPaths = ['/login', '/portal', '/onboard', '/apply', '/register', '/p/sign/owner'];
    
    if (!publicPaths.some(p => path.includes(p))) {
        window.location.href = '/login';
    }
};

// Add App Check token and Auth token to requests
api.interceptors.request.use(
    async (config) => {
        if (appCheck) {
            try {
                const appCheckTokenResponse = await Promise.race([
                    getToken(appCheck),
                    new Promise((_, reject) => setTimeout(() => reject(new Error('appcheck-timeout')), 2000))
                ]);
                config.headers['X-Firebase-AppCheck'] = appCheckTokenResponse.token;
            } catch {
                // non-fatal — continue without App Check token
            }
        }

        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response Interceptor for Global Error Handling
let subscriptionRequiredHandler = null;

export const setSubscriptionRequiredHandler = (handler) => {
    subscriptionRequiredHandler = handler;
};

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const config = error.config;

        if (error.response) {
            const { status, data } = error.response;

            if (status === 429) {
                toast.error(data?.message || 'Too many attempts. Please wait a moment.', {
                    id: 'rate-limit-toast',
                    duration: 5000
                });
            }

            // Silent token refresh on 401 — retry the original request once
            if (status === 401 && !config._authRetried && !config.url?.includes('/auth/refresh') && !config.url?.includes('/auth/login') && !config.url?.includes('/auth/onboard') && !config.url?.includes('/portal/auth/')) {
                config._authRetried = true;
                try {
                    const newToken = await refreshAccessToken();
                    if (newToken) {
                        config.headers.Authorization = `Bearer ${newToken}`;
                        return api(config);
                    }
                } catch {
                    // refresh failed — fall through to logout
                }
                handleLogout();
                return Promise.reject(error);
            }

            if (status === 401 && !config.url?.includes('/auth/login') && !config.url?.includes('/auth/onboard') && !config.url?.includes('/portal/auth/')) {
                handleLogout();
            } else if (status === 403) {
                if (data?.code === 'SUBSCRIPTION_REQUIRED') {
                    if (subscriptionRequiredHandler) {
                        subscriptionRequiredHandler();
                        return Promise.reject(error);
                    }
                } else if (data?.code !== 'FEATURE_LOCKED') {
                    toast.error(data?.message || 'You do not have permission to perform this action.', { id: 'permission-error' });
                }
            }
        } else if (error.request) {
            // Cloud Run cold starts — retry GET once after 1.2s
            if (config && config.method === 'get' && !config._coldStartRetried) {
                config._coldStartRetried = true;
                return new Promise(resolve => setTimeout(resolve, 1200))
                    .then(() => api(config));
            }
            toast.error('Network error. Please check your connection.', { id: 'network-error' });
        }

        // Capture unexpected 5xx errors in Sentry for observability
        if (error.response?.status >= 500) {
            Sentry.captureException(error, {
                extra: {
                    url: config?.url,
                    method: config?.method,
                    status: error.response.status,
                }
            });
        }

        return Promise.reject(error);
    }
);

// Subscription Payments API (Needed for Admin components)
export const subscriptionPaymentsAPI = {
    getAll: async (filters = {}) => {
        const response = await api.get('/admin/subscription-payments', { params: filters });
        return response.data;
    },
    approve: async (id) => {
        const response = await api.put(`/admin/subscription-payments/${id}/approve`);
        return response.data;
    },
    reject: async (id, reason) => {
        const response = await api.put(`/admin/subscription-payments/${id}/reject`, { reason });
        return response.data;
    },
    getHistory: async () => {
        const response = await api.get('/subscriptions/history');
        return response.data;
    },
    getStatus: async () => {
        const response = await api.get('/subscriptions/status');
        return response.data;
    },
    createPortalSession: async () => {
        const response = await api.post('/stripe/create-portal-session');
        return response.data;
    },
    createCheckoutSession: async (priceId) => {
        const response = await api.post('/stripe/create-checkout-session', { priceId });
        return response.data;
    }
};

// Bookings API
export const bookingsAPI = {
    getAll: async (params) => {
        const response = await api.get('/bookings', { params });
        return response.data;
    },
    getById: async (id) => {
        const response = await api.get(`/bookings/${id}`);
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/bookings', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/bookings/${id}`, data);
        return response.data;
    },
    confirm: async (id, data = {}) => {
        const response = await api.put(`/bookings/${id}/confirm`, data);
        return response.data;
    },
    cancel: async (id, reason) => {
        const response = await api.delete(`/bookings/${id}`, { data: { reason } });
        return response.data;
    },
    convertToTenant: async (id, data) => {
        const response = await api.post(`/bookings/${id}/convert`, data);
        return response.data;
    },
    checkConflicts: async (data) => {
        const response = await api.post('/bookings/check-conflicts', data);
        return response.data;
    },
    getStats: async (params) => {
        const response = await api.get('/bookings/stats', { params });
        return response.data;
    }
};

// Waitlist API
export const waitlistAPI = {
    getAll: async (params) => {
        const response = await api.get('/waitlist', { params });
        return response.data;
    },
    add: async (data) => {
        const response = await api.post('/waitlist', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/waitlist/${id}`, data);
        return response.data;
    },
    remove: async (id, reason) => {
        const response = await api.delete(`/waitlist/${id}`, { data: { reason } });
        return response.data;
    },
    process: async (id) => {
        const response = await api.put(`/waitlist/${id}/process`);
        return response.data;
    }
};

// Tenants API
export const tenantsAPI = {
    getAll: async (params) => {
        const response = await api.get('/tenants', { params });
        return response.data;
    },
    getById: async (id) => {
        const response = await api.get(`/tenants/${id}`);
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/tenants', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/tenants/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/tenants/${id}`);
        return response.data;
    },
    endLease: async (id) => {
        const response = await api.post(`/tenants/${id}/end-lease`);
        return response.data;
    },
    extendLease: async (id, newEndDate) => {
        const response = await api.post(`/tenants/${id}/extend`, { newEndDate });
        return response.data;
    },
    renewLease: async (id, data) => {
        const response = await api.post(`/tenants/${id}/renew`, data);
        return response.data;
    },
    transferApartment: async (id, data) => {
        const response = await api.post(`/tenants/${id}/transfer`, data);
        return response.data;
    },
    getRentPayments: async (id, params) => {
        const response = await api.get(`/tenants/leases/${id}/payments`, { params });
        return response.data;
    },
    updateMaintenanceRequestStatus: async (id, data) => {
        const response = await api.put(`/tenants/maintenance/${id}`, data);
        return response.data;
    },
    // ── Ledger ────────────────────────────────────────────────────────────────
    getLedger: async (tenantId, params = {}) => {
        const response = await api.get(`/tenants/${tenantId}/ledger`, { params });
        return response.data;
    },
    addLedgerCharge: async (tenantId, data) => {
        const response = await api.post(`/tenants/${tenantId}/ledger/charge`, data);
        return response.data;
    },
    addLedgerCredit: async (tenantId, data) => {
        const response = await api.post(`/tenants/${tenantId}/ledger/credit`, data);
        return response.data;
    },
    generateMonthlyCharge: async (tenantId) => {
        const response = await api.post(`/tenants/${tenantId}/ledger/generate-monthly`);
        return response.data;
    },
    applyLateFee: async (tenantId, data) => {
        const response = await api.post(`/tenants/${tenantId}/ledger/late-fee`, data);
        return response.data;
    }
};

// Keys API
export const keysAPI = {
    getHistory: async (apartmentId) => {
        const response = await api.get(`/keys/${apartmentId}/history`);
        return response.data;
    },
    recordHandover: async (data) => {
        const response = await api.post('/keys/handover', data);
        return response.data;
    }
};

export default api;

